/**
 * PanelStaleNote.tsx
 *
 * Date-meta footer shown at the bottom of panels. Two modes:
 *
 *   - **Stale (PR 6 — AUTH_EXPIRED path):** amber "Data as of [date]"
 *     footnote when the dashboard has stale data (last sync failed and
 *     data hasn't been refreshed). Mockup 30 / brand guide §31.
 *
 *   - **Cached (Slice 3.2 — credential-aware availability):** muted
 *     "Last synced [date] (cached)" footnote when this panel's backing
 *     domain was SKIPPED on the most recent sync because the credential
 *     probed as `unavailable`. The BE preserved the panel's cached
 *     `dashboard_panel_data` row bit-identically (TDD §0.3 invariant).
 *     The "(cached)" suffix is the ONLY visible per-panel marker
 *     — no dimming, no border change, no placeholder icon (brand guide §37f).
 *
 * This component is purely presentational: it receives a pre-formatted
 * date string and a mode flag and renders the appropriate note. The caller
 * is responsible for deciding when to show it.
 *
 * Accessibility: role="note" — informational, not an error alert.
 */

import React from 'react'
import { AlertTriangle } from 'lucide-react'

interface PanelStaleNoteProps {
  /**
   * Human-readable date string for the last successful sync.
   * e.g. "Apr 22, 2026"
   */
  asOf: string
  /**
   * Slice 3.2 — credential-aware availability.
   *
   * When true, render the muted "(cached)" variant — this panel's backing
   * domain was SKIPPED on the most recent sync (the BE preserved the cached
   * row per the §0.3 invariant). When false (or omitted), render the
   * amber stale variant (PR 6 AUTH_EXPIRED path).
   *
   * The cached variant is intentionally less alarming than the stale
   * variant: stale = "we tried to sync and the whole thing failed"; cached
   * = "we ran a partial sync and intentionally preserved your data while
   * one source needs attention". The §37f banner above carries the
   * actionable copy + CTA; the panel-level marker stays understated.
   *
   * Brand guide §37f.
   */
  cached?: boolean
}

export const PanelStaleNote: React.FC<PanelStaleNoteProps> = ({
  asOf,
  cached = false,
}) =>
  cached ? (
    // Cached variant (Slice 3.2) — muted, no warning icon, no amber tint.
    // The §37f banner above provides the alert context; this is just a
    // factual "last refreshed" marker with the "(cached)" suffix.
    <div
      className="flex items-center gap-1 mt-2 text-[10.5px] text-muted-foreground"
      role="note"
      data-testid="panel-cached-note"
    >
      <span>
        Last synced {asOf} <span className="font-medium">(cached)</span>
      </span>
    </div>
  ) : (
    // Stale variant (PR 6) — amber, with warning icon.
    <div
      className="flex items-center gap-1 mt-2 text-[10.5px] text-warning"
      role="note"
      data-testid="panel-stale-note"
    >
      <AlertTriangle size={11} className="flex-shrink-0" aria-hidden="true" />
      <span>Data as of {asOf}</span>
    </div>
  )
